/**
 * @file Photo service — capture, compress, upload to Supabase Storage
 */

import { getClient } from '@state/supabase.js';
import { getUserId } from '@state/store.js';
import { addJobPhoto, getJobById } from './jobs.js';

/**
 * Capture a photo using file input (Capacitor Camera in future rounds).
 * @returns {Promise<string|null>} base64 data URL or null
 */
export function capturePhoto() {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) { resolve(null); input.remove(); return; }
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
        input.remove();
      };
      reader.onerror = () => { resolve(null); input.remove(); };
      reader.readAsDataURL(file);
    });

    input.addEventListener('cancel', () => { resolve(null); input.remove(); });
    input.click();
  });
}

/**
 * Compress a base64 image using canvas.
 * @param {string} base64 - data URL
 * @param {number} [maxWidth=1200]
 * @param {number} [quality=0.8]
 * @returns {Promise<string>} compressed base64
 */
export function compressPhoto(base64, maxWidth = 1200, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      if (w > maxWidth) {
        h = Math.round(h * (maxWidth / w));
        w = maxWidth;
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = base64;
  });
}

/**
 * Upload a photo to Supabase Storage.
 * @param {string} jobId
 * @param {string} base64 - data URL
 * @returns {Promise<string|null>} public URL or null
 */
export async function uploadPhoto(jobId, base64) {
  const sb = getClient();
  const userId = getUserId();
  if (!sb || !userId) return null;

  // Convert base64 to blob
  const res = await fetch(base64);
  const blob = await res.blob();

  const timestamp = Date.now();
  const path = `job-photos/${userId}/${jobId}/${timestamp}.jpg`;

  try {
    const { error } = await sb.storage.from('photos').upload(path, blob, {
      contentType: 'image/jpeg',
      cacheControl: '3600',
    });
    if (error) {
      console.warn('[photos] Upload error:', error.message);
      return null;
    }

    const { data: urlData } = sb.storage.from('photos').getPublicUrl(path);
    return urlData?.publicUrl || null;
  } catch (e) {
    console.warn('[photos] Upload exception:', e.message);
    return null;
  }
}

/**
 * Capture, compress, upload, and attach a photo to a job.
 * @param {string} jobId
 * @returns {Promise<string|null>} public URL or null
 */
export async function captureAndAttach(jobId) {
  const raw = await capturePhoto();
  if (!raw) return null;

  const compressed = await compressPhoto(raw);
  const url = await uploadPhoto(jobId, compressed);
  if (url) {
    addJobPhoto(jobId, url);
  }
  return url;
}

/**
 * Get photos for a job.
 */
export function getJobPhotos(jobId) {
  // Photos are stored on the job card in jobs_pipeline
  const job = getJobById(jobId);
  return job?.photos || [];
}
